// EditProduct.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CloudUpload, Loader2 } from 'lucide-react';
import axios from 'axios';

const EditProduct: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form States
  const [productName, setProductName] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [gemstoneType, setGemstoneType] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [weight, setWeight] = useState('');
  const [gender, setGender] = useState(''); // Added Gender State
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [currentTagInput, setCurrentTagInput] = useState('');

  const [makingCharges, setMakingCharges] = useState('');
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');
  const [purity, setPurity] = useState('');

  // Image States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState(''); 
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Pricing States
  const [selectedCity, setSelectedCity] = useState<string>('Delhi');
  const [liveGold24KPrice, setLiveGold24KPrice] = useState<number | null>(null);
  const [liveSilverPriceKg, setLiveSilverPriceKg] = useState<number | null>(null);
  const [calculatedFinalPrice, setCalculatedFinalPrice] = useState<number | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const productCategories = [
    'Rings', 'Necklaces', 'Earrings', 'Bracelets', 'Pendants', 'Anklets',
    'Bangles', 'Chains', 'Mangalsutras', 'Nose Pins', 'Toe Rings', 'Watches',
    'Kids Jewelry', 'Mens Jewelry', 'Others'
  ];

  const ringSizes = ['US 5', 'US 6', 'US 7', 'US 8', 'US 9', 'US 10'];
  const necklaceBraceletLengths = ['16 inch', '18 inch', '20 inch', 'Adjustable'];

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSimulatedPrices = useCallback((city: string) => {
    let baseGold24K_per_gram = 11090;
    let baseSilverKg = 90000;
    const newGold24K = parseFloat((baseGold24K_per_gram + Math.random() * 50).toFixed(2));
    const newSilverKg = parseFloat((baseSilverKg + Math.random() * 100).toFixed(2));

    return { gold24K: newGold24K, silverKg: newSilverKg };
  }, []);

  // Fetch Existing Product Data (MODIFIED TO FIX BACKEND GET /{id} ISSUE)
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        // Workaround: Since GET by ID is throwing 405 Method Not Allowed, 
        // we fetch all products and filter the one we need.
        const response = await axios.get(`https://sardar-jewellers.com/api/admin/${type}-products`);
        const allProducts = response.data;
        
        // Find the specific product based on 'srno'
        const data = allProducts.find((item: any) => item.srno.toString() === id);

        if (!data) {
          setErrorMessage("Product not found in the list.");
          setLoading(false);
          return;
        }
        
        // Populate states with existing data
        setProductName(data.itemName || '');
        setMaterialType(type === 'gold' ? 'Gold' : 'Silver');
        setGemstoneType(data.gemstoneType || '');
        setProductCategory(data.category || '');
        setWeight(data.weight?.toString() || '');
        setGender(data.gender || ''); // Populate gender if it exists
        setDescription(data.description || '');
        setSku(data.sku || '');
        setStockQuantity(data.stockQuantity?.toString() || '');
        setMakingCharges(data.makingCharges?.toString() || '');
        setDiscount(data.discount?.toString() || '');
        setTax(data.tax?.toString() || '');
        setPurity(data.purity || '');
        setSelectedCity(data.city || 'Delhi');
        setCalculatedFinalPrice(data.price || 0);

        if (data.images && data.images.length > 0) {
            setImageUrl(data.images[0].imagePath);
            setPreviewImage(data.images[0].imagePath);
        } else if (data.imageUrl) {
            setImageUrl(data.imageUrl);
            setPreviewImage(data.imageUrl);
        }
      } catch (err) {
        console.error("Error fetching product details", err);
        setErrorMessage("Failed to load product details.");
      } finally {
        setLoading(false);
      }
    };
    if (id && type) fetchProductDetails();
  }, [id, type]);

  // Live Price Simulation Effect
  useEffect(() => {
    const simulatedData = getSimulatedPrices(selectedCity);
    setLiveGold24KPrice(simulatedData.gold24K);
    setLiveSilverPriceKg(simulatedData.silverKg);
  }, [selectedCity, getSimulatedPrices]);

  // Price Calculation Effect
  useEffect(() => {
    let baseMetalCost = 0;
    const parsedWeight = parseFloat(weight);
    const parsedMakingCharges = parseFloat(makingCharges) || 0;
    const parsedDiscount = parseFloat(discount) || 0;
    const parsedTax = parseFloat(tax) || 0;

    if (parsedWeight > 0 && materialType && liveGold24KPrice !== null && liveSilverPriceKg !== null) {
      if (materialType === 'Gold' && purity) {
        let purityFactor = purity === '24K' ? 1 : purity === '22K' ? 22/24 : 18/24;
        baseMetalCost = liveGold24KPrice * purityFactor * parsedWeight;
      } else if (materialType === 'Silver') {
        baseMetalCost = (liveSilverPriceKg / 1000) * parsedWeight;
      }
      let priceBeforeDiscount = baseMetalCost + parsedMakingCharges;
      let finalPrice = (priceBeforeDiscount * (1 - (parsedDiscount / 100))) * (1 + (parsedTax / 100));
      setCalculatedFinalPrice(parseFloat(finalPrice.toFixed(2)));
    }
  }, [materialType, purity, weight, makingCharges, discount, tax, liveGold24KPrice, liveSilverPriceKg]);

  const handleSizeToggle = (size: string) => {
    setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  };

  const handleFileChangeForPreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('files', file);
    try {
      const response = await fetch('https://sardar-jewellers.com/api/upload/images', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error(`Upload failed`);
      const uploadedPaths: string[] = await response.json();
      return uploadedPaths && uploadedPaths.length > 0 ? uploadedPaths[0] : null;
    } catch (error) {
      setErrorMessage(`Error uploading image.`);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    setSaving(true);

    let finalImageUrl = imageUrl;
    if (selectedFile) {
      const uploadedPath = await uploadImage(selectedFile);
      if (uploadedPath) {
        // finalImageUrl = `http://localhost:8080${uploadedPath}`;
        finalImageUrl = `https://sardar-jewellers.com${uploadedPath}`;
      } else {
        setSaving(false);
        return;
      }
    }

    const updatedProductData = {
      itemName: productName,
      weight: parseFloat(weight),
      price: calculatedFinalPrice,
      makingCharges: parseFloat(makingCharges) || 0,
      description: description,
      category: productCategory,
      gemstoneType: gemstoneType,
      gender: gender, // Added Gender to update payload
      sku: sku,
      stockQuantity: parseInt(stockQuantity),
      discount: discount ? parseFloat(discount) : 0,
      tax: tax ? parseFloat(tax) : 0,
      imageUrl: finalImageUrl,
      purity: purity,
      city: selectedCity,
    };

    // const endpoint = `http://localhost:8080/api/admin/${type}-products/${id}`;
    const endpoint = `https://sardar-jewellers.com/api/admin/${type}-products/${id}`;

    try {
      const bodyToSend = {
        product: updatedProductData,
        imagePaths: [finalImageUrl],
      };

      const response = await fetch(endpoint, {
        method: 'PUT', // Changed to PUT for updating
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyToSend),
      });

      if (!response.ok) throw new Error(`Failed to update product.`);

      setSuccessMessage('Product updated successfully! Redirecting...');
      setTimeout(() => navigate('/admin/products/list'), 1500); // Route back to list
    } catch (error: any) {
      setErrorMessage(`Error updating product: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="animate-spin h-10 w-10 text-yellow-600" /></div>;

  return (
    <div className="flex p-8 bg-gray-100 min-h-screen">
      {successMessage && <div className="fixed top-4 right-4 bg-green-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">{successMessage}</div>}
      {errorMessage && <div className="fixed top-4 right-4 bg-red-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">{errorMessage}</div>}
      
      {/* Left Sidebar - Product Preview Card */}
      <div className="w-1/4 pr-8">
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Product Preview</h3>
          {previewImage || imageUrl ? (
            <img src={previewImage || imageUrl} alt="Product Preview" className="w-full h-48 object-contain mb-4 rounded-md border border-gray-200" />
          ) : (
            <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-500 rounded-md mb-4">No Image</div>
          )}
          <h3 className="text-lg font-semibold text-gray-900">{productName || 'Jewelry Item Name'}</h3>
          <p className="text-sm text-gray-600 mb-2">Category: {productCategory || 'N/A'}</p>
          <div className="flex items-baseline mb-3">
            <span className="text-red-600 font-bold text-xl">{formatCurrency(calculatedFinalPrice)}</span>
            {discount && parseFloat(discount) > 0 && <span className="ml-2 text-sm text-red-500">({discount}% Off)</span>}
          </div>
        </div>

        <div className="flex space-x-4">
          <button type="submit" form="edit-product-form" disabled={saving} className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-4 rounded-lg transition text-center flex justify-center items-center">
            {saving ? <Loader2 className="animate-spin mr-2 h-5 w-5" /> : 'Update Product'}
          </button>
          <button type="button" className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded-lg transition text-center" onClick={() => navigate('/admin/products/list')}>
            Cancel
          </button>
        </div>
      </div>

      {/* Main Content - Product Form */}
      <div className="w-3/4">
        <form onSubmit={handleSubmit} id="edit-product-form">
          {/* Photo Section */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Update Product Photo</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-gray-400" onClick={() => document.getElementById('fileInput')?.click()}>
              {previewImage || imageUrl ? (
                <img src={previewImage || imageUrl} alt="Uploaded Preview" className="max-h-60 mx-auto object-contain mb-4" />
              ) : (
                <CloudUpload className="mx-auto w-16 h-16 text-gray-400 mb-4" />
              )}
              <input id="fileInput" type="file" accept="image/png, image/jpeg" className="hidden" onChange={handleFileChangeForPreview} />
              <p className="text-sm text-gray-500 mt-2">Click to change image</p>
            </div>
          </div>

          {/* Info Section */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Product Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                <input type="text" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={productName} onChange={(e) => setProductName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Type</label>
                <input type="text" className="w-full border-gray-300 rounded-md shadow-sm border p-2 bg-gray-100 cursor-not-allowed" value={materialType} disabled />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gemstone Type</label>
                <input type="text" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={gemstoneType} onChange={(e) => setGemstoneType(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Category *</label>
                <select className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={productCategory} onChange={(e) => setProductCategory(e.target.value)} required>
                  <option value="">Select Category</option>
                  {productCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (in grams) *</label>
                <input type="number" step="0.01" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={weight} onChange={(e) => setWeight(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purity *</label>
                <select className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={purity} onChange={(e) => setPurity(e.target.value)} required>
                  <option value="">Select Purity</option>
                  <option value="18K">18K</option>
                  <option value="22K">22K</option>
                  <option value="24K">24K</option>
                  <option value="925">925</option>
                  <option value="999">999</option>
                </select>
              </div>
              {/* Gender Dropdown added to match Create Product UI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={gender} onChange={(e) => setGender(e.target.value)} required>
                  <option value="">Select Gender</option>
                  <option value="Unisex">Unisex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Description *</label>
              <textarea rows={4} className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={description} onChange={(e) => setDescription(e.target.value)} required></textarea>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                <input type="text" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={sku} onChange={(e) => setSku(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity *</label>
                <input type="number" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={stockQuantity} onChange={(e) => setStockQuantity(e.target.value)} required />
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Pricing & Live Rate Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Making Charges</label>
                <input type="number" step="0.01" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={makingCharges} onChange={(e) => setMakingCharges(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                <input type="number" step="0.01" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={discount} onChange={(e) => setDiscount(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax (%)</label>
                <input type="number" step="0.01" className="w-full border-gray-300 rounded-md shadow-sm border p-2 focus:border-yellow-500 focus:ring-yellow-500" value={tax} onChange={(e) => setTax(e.target.value)} />
              </div>
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Calculated Product Price:</h3>
                <p className="text-3xl font-extrabold text-green-700">{formatCurrency(calculatedFinalPrice)}</p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;